'use client'

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import styles from './carousel.module.css';

const CarouselContext = createContext(null);

function joinClassNames(...values) {
  return values.filter(Boolean).join(' ');
}

function useCarousel() {
  const context = useContext(CarouselContext);

  if (!context) {
    throw new Error('Carousel components must be used inside <Carousel />');
  }

  return context;
}

export const Carousel = React.forwardRef(function Carousel(
  {
    children,
    className,
    opts,
    plugins,
    orientation = 'horizontal',
    setApi,
    ...props
  },
  ref,
) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === 'horizontal' ? 'x' : 'y',
    },
    plugins,
  );
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback((currentApi) => {
    if (!currentApi) return;

    setCanScrollPrev(currentApi.canScrollPrev());
    setCanScrollNext(currentApi.canScrollNext());
  }, []);

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  useEffect(() => {
    if (!api || !setApi) return undefined;

    setApi(api);

    return () => {
      setApi(undefined);
    };
  }, [api, setApi]);

  useEffect(() => {
    if (!api) return undefined;

    onSelect(api);
    api.on('reInit', onSelect);
    api.on('select', onSelect);

    return () => {
      api.off('reInit', onSelect);
      api.off('select', onSelect);
    };
  }, [api, onSelect]);

  const value = useMemo(() => ({
    carouselRef,
    orientation,
    scrollPrev,
    scrollNext,
    canScrollPrev,
    canScrollNext,
  }), [carouselRef, orientation, scrollPrev, scrollNext, canScrollPrev, canScrollNext]);

  return (
    <CarouselContext.Provider value={value}>
      <div ref={ref} className={joinClassNames(styles.root, className)} {...props}>
        {children}
      </div>
    </CarouselContext.Provider>
  );
});

export const CarouselContent = React.forwardRef(function CarouselContent(
  { children, className },
  ref,
) {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div ref={carouselRef} className={styles.viewport}>
      <div
        ref={ref}
        className={joinClassNames(
          styles.container,
          orientation === 'vertical' && styles.containerVertical,
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
});

export const CarouselItem = React.forwardRef(function CarouselItem(
  { children, className },
  ref,
) {
  const { orientation } = useCarousel();

  return (
    <div
      ref={ref}
      role="group"
      aria-roledescription="slide"
      className={joinClassNames(
        styles.item,
        orientation === 'vertical' && styles.itemVertical,
        className,
      )}
    >
      {children}
    </div>
  );
});

export const CarouselPrevious = React.forwardRef(function CarouselPrevious(
  { className, ...props },
  ref,
) {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel();

  return (
    <button
      ref={ref}
      type="button"
      className={joinClassNames(styles.button, styles.buttonPrev, className)}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      aria-label={orientation === 'horizontal' ? 'Previous slide' : 'Previous item'}
      {...props}
    >
      <FiChevronLeft size={18} />
    </button>
  );
});

export const CarouselNext = React.forwardRef(function CarouselNext(
  { className, ...props },
  ref,
) {
  const { orientation, scrollNext, canScrollNext } = useCarousel();

  return (
    <button
      ref={ref}
      type="button"
      className={joinClassNames(styles.button, styles.buttonNext, className)}
      disabled={!canScrollNext}
      onClick={scrollNext}
      aria-label={orientation === 'horizontal' ? 'Next slide' : 'Next item'}
      {...props}
    >
      <FiChevronRight size={18} />
    </button>
  );
});
